/**
 * Manda un documento HTML a imprimir. Se carga en un iframe oculto y se llama a
 * su print(): asi solo sale el documento (no la aplicacion) y los estilos de
 * pagina (@page) del propio documento mandan sobre el tamaño de papel.
 *
 * Por defecto el navegador abre su dialogo de impresion. Para que salga directo
 * a la impresora predeterminada, sin dialogo, el navegador de la caja se
 * arranca con --kiosk-printing (Chrome/Edge).
 *
 * La promesa se resuelve cuando el dialogo se ha cerrado (print() es sincrono en
 * los navegadores de escritorio) o rechaza si no se pudo abrir.
 */
export interface PrintOptions {
    /**
     * Papel continuo (rollo termico): ancho del rollo en mm. El alto se ajusta al
     * contenido. Chrome no admite `@page { size: 80mm auto }`, asi que sin esto el
     * ticket saldria en una hoja Letter.
     */
    rollWidthMm?: number;
}

const PX_PER_MM = 96 / 25.4;
/** Margen del @page del ticket (3 mm por lado) mas un respiro para que no corte la ultima linea. */
const ROLL_EXTRA_MM = 10;

/** Fija el tamaño de pagina de un documento a un rollo de `widthMm` de ancho y el alto de su contenido. */
export const applyRollPageSize = (doc: Document, widthMm: number): number => {
    // offsetHeight del body y no scrollHeight: este ultimo nunca baja del alto de la
    // ventana donde se carga, y el ticket saldria mas largo que su contenido.
    const contentPx = doc.body ? doc.body.offsetHeight : 0;
    const heightMm = Math.ceil(contentPx / PX_PER_MM + ROLL_EXTRA_MM);

    const style = doc.createElement('style');
    style.setAttribute('data-roll-page', '');
    style.textContent = `@page { size: ${widthMm}mm ${heightMm}mm; margin: 3mm; }`;
    doc.head.appendChild(style);
    return heightMm;
};

export const printHtml = (html: string, options: PrintOptions = {}): Promise<void> =>
    new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        // Fuera de pantalla pero con ancho real: con width:0 el contenido se maqueta
        // sin ancho y el alto que se mide para el ticket saldria desbordado.
        iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:340px;height:100px;border:0;visibility:hidden';

        let finished = false;
        const finish = (error?: Error) => {
            if (finished) return;
            finished = true;
            // Se retira un rato despues: algunos navegadores siguen leyendo el
            // documento mientras generan la cola de impresion.
            setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1000);
            if (error) reject(error);
            else resolve();
        };

        let started = false;
        iframe.onload = () => {
            // Un solo trabajo por documento, aunque el navegador dispare 'load' mas de
            // una vez (el iframe parte de about:blank antes de cargar el contenido).
            if (started) return;
            const doc = iframe.contentDocument;
            if (!doc || !doc.body || doc.body.innerHTML.trim().length === 0) return;
            started = true;
            try {
                const win = iframe.contentWindow;
                if (!win) throw new Error('No se pudo abrir la ventana de impresión.');
                if (options.rollWidthMm) applyRollPageSize(win.document, options.rollWidthMm);
                win.focus();
                win.print();
                finish();
            } catch (err) {
                finish(err as Error);
            }
        };

        iframe.srcdoc = html;
        document.body.appendChild(iframe);
    });
